import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from "@reduxjs/toolkit";
import axios from "../../axiosConfig";
import {
  Listing,
  ListingLocation,
  Connection,
  ListingNewWithDetailsDict,
  ConnectionWithId,
  ListingWithDetails,
  EnrichedConnection,
} from "../../types";
import { enrichConnections } from "../../modules/enrich-connections";

interface ListingsState {
  byId: Record<string, Listing>;
  allIds: string[];
  connections: Connection[];
  listingLocations: ListingLocation[];
  loading: boolean;
  error: string | null;
}

const initialState: ListingsState = {
  byId: {},
  allIds: [],
  connections: [],
  listingLocations: [],
  loading: false,
  error: null,
};

export const fetchListings = createAsyncThunk(
  "listings/fetchListings",
  async () => {
    const response = await axios.get(`/api/listings/`);
    return response.data;
  }
);

// Fetch connections between listings
export const fetchConnections = createAsyncThunk(
  "listings/fetchConnections",
  async () => {
    const response = await axios.get("/api/connections");
    const ret = response.data;
    return ret;
  }
);

// Fetch listing locations
export const fetchListingLocations = createAsyncThunk(
  "listings/fetchListingLocations",
  async () => {
    const response = await axios.get("/api/listings-locations");
    const ret = response.data;
    return ret;
  }
);

// Delete a listing
export const deleteListing = createAsyncThunk(
  "listings/deleteListing",
  async (listingId: string) => {
    await axios.delete(`/api/listings/${listingId}`);
    return listingId;
  }
);

// Update a listing
export const updateListing = createAsyncThunk(
  "listings/updateListing",
  async ({ listingId, data }: { listingId: string; data: Partial<Listing> }) => {
    const response = await axios.put(`/api/listings/${listingId}`, data);
    return response.data as Listing;
  }
);

const listingsSlice = createSlice({
  name: "listings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchListings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchListings.fulfilled,
        (state, action: PayloadAction<Listing[]>) => {
          state.loading = false;
          state.byId = {};
          state.allIds = [];

          action.payload.forEach((listing) => {
            state.byId[listing.id] = listing;
            state.allIds.push(listing.id);
          });
        }
      )
      .addCase(fetchListings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch listings";
      })

      .addCase(fetchConnections.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchConnections.fulfilled,
        (state, action: PayloadAction<Connection[]>) => {
          state.loading = false;
          state.connections = action.payload;
        }
      )
      .addCase(fetchConnections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch connections";
      })
      .addCase(fetchListingLocations.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchListingLocations.fulfilled,
        (state, action: PayloadAction<ListingLocation[]>) => {
          state.loading = false;
          state.listingLocations = action.payload;
        }
      )
      .addCase(fetchListingLocations.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.error.message || "Failed to fetch listing locations";
      })
      .addCase(deleteListing.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteListing.fulfilled,
        (state, action: PayloadAction<string>) => {
          state.loading = false;
          const deletedId = action.payload;
          delete state.byId[deletedId];
          state.allIds = state.allIds.filter((id) => id !== deletedId);
        }
      )
      .addCase(deleteListing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete listing";
      })
      .addCase(updateListing.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateListing.fulfilled,
        (state, action: PayloadAction<Listing>) => {
          state.loading = false;
          const updatedListing = action.payload;
          state.byId[updatedListing.id] = updatedListing;
        }
      )
      .addCase(updateListing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update listing";
      });
  },
});

// SELECTORS

// Select an listing by ID, returning also its associated locations and members
// Nested selector that takes state, then listing ID
export const selectListingById =
  (id: string) =>
  (state: { listings: ListingsState }): Listing | null => {
    const listing = state.listings.byId[id];
    if (!listing) return null;
    return listing;
  };

// Input selectors
const selectAllIds = (state: { listings: ListingsState }) =>
  state.listings.allIds;
const selectListingsById = (state: { listings: ListingsState }) =>
  state.listings.byId;
export const selectConnections = (state: { listings: ListingsState }) =>
  state.listings.connections;
const selectListingLocations = (state: { listings: ListingsState }) =>
  state.listings.listingLocations;
const selectLocationsById = (state: any) => state.locations?.byId || {};

// Select listings with locations and connections joined
export const selectListingsWithDetails = createSelector(
  [
    selectAllIds,
    selectListingsById,
    selectConnections,
    selectListingLocations,
    selectLocationsById,
  ],
  (
    allIds,
    byId,
    connections,
    listingLocations,
    locationsById
  ): ListingNewWithDetailsDict => {
    const arr = allIds.map((id) => {
      const listing = byId[id];
      const locations = listingLocations?.filter((ll) => ll.listingId === id);
      const listingConnections: Connection[] =
        connections?.filter(
          (conn) => conn.subjectListingId === id || conn.objectListingId === id
        ) || [];

      const listingConnectionsWithId: ConnectionWithId[] =
        listingConnections.map((conn) => {
          // The id is the 'to' node, the one that's not the current listing
          const connectionId =
            conn.subjectListingId === id
              ? conn.objectListingId
              : conn.subjectListingId;
          return {
            ...conn,
            id: connectionId,
            connectionType: conn.roleType,
          };
        });

      // Get location name from the first location
      const firstLocationId = locations?.[0]?.locationId;
      const locationName =
        firstLocationId && locationsById[firstLocationId]
          ? locationsById[firstLocationId].placeName
          : "Location TBD";

      return {
        ...listing,
        locationName,
        locations: locations || [],
        connections: listingConnectionsWithId,
      };
    });
    return arr.reduce((acc, listing) => {
      acc[listing.id] = listing;
      return acc;
    }, {} as ListingNewWithDetailsDict);
  }
);

// Select listing with details by ID
export const selectListingWithDetailsById =
  (id: string) =>
  (state: { listings: ListingsState }): ListingWithDetails | null => {
    const listingsWithDetails = selectListingsWithDetails(state);
    return listingsWithDetails[id] || null;
  };

// Select enriched connections for a specific listing
// Returns an array of connections with full listing details
// Properly memoized using createSelector
export const selectEnrichedConnectionsForListing = createSelector(
  [
    selectConnections,
    selectListingsWithDetails,
    (_state: { listings: ListingsState }, listingId: string | undefined) =>
      listingId,
  ],
  (connections, listingsWithDetails, listingId): EnrichedConnection[] => {
    if (!listingId) return [];
    return enrichConnections(listingId, connections, listingsWithDetails);
  }
);

export default listingsSlice.reducer;
